import { useEffect, useRef, useState, type RefObject } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Clock, Mail, Plus, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  AUTOMATION_PLATFORMS,
  type AutomationPlatform,
  type DayOfWeek,
} from '@backend/lib/db/schema/automation'
import { DotGrid, emitDotRipple } from '@/pages/moodboard-page/canvas/dot-grid'
import { SNAP_CURVE } from '@/pages/moodboard-page/canvas/motion-tokens'
import { StarsIcon } from '@/components/ui/icons/icons-new'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { PLATFORM_LABELS } from './constants'
import { formatDayList } from './schedule'
import { PlatformIcon } from './platform-icon'
import { SourceChip } from './source-chip'
import type { AutomationSource } from './source'

export type FlowSelection = 'trigger' | 'generate' | 'email' | `platform:${AutomationPlatform}`

/**
 * Owns the cursor + viewport state and renders ONLY the DotGrid, so pointer
 * moves (which feed cursor magnetism) re-render this leaf rather than the whole
 * node tree. Tracks the pane via a shared ref so it stays pinned under the flow.
 */
function DotGridLayer({ paneRef }: { paneRef: RefObject<HTMLDivElement | null> }) {
  const [viewport, setViewport] = useState({ width: 0, height: 0 })
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null)

  useEffect(() => {
    const el = paneRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect
      if (rect) setViewport({ width: rect.width, height: rect.height })
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [paneRef])

  useEffect(() => {
    const el = paneRef.current
    if (!el) return
    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect()
      setCursor({ x: e.clientX - rect.left, y: e.clientY - rect.top })
    }
    const onLeave = () => setCursor(null)
    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerleave', onLeave)
    return () => {
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerleave', onLeave)
    }
  }, [paneRef])

  // Static view of the moodboard dot grid: identity transform, no pan/zoom.
  return (
    <DotGrid
      scale={1}
      offset={{ x: 0, y: 0 }}
      viewport={viewport}
      cursorWorld={cursor}
      dragHalos={[]}
    />
  )
}

interface Props {
  days: DayOfWeek[]
  timezone: string
  source: AutomationSource | undefined
  platforms: AutomationPlatform[]
  selected: FlowSelection
  onSelect: (selection: FlowSelection) => void
  onAddPlatform: (platform: AutomationPlatform) => void
  onRemovePlatform: (platform: AutomationPlatform) => void
  className?: string
}

// Moodboard item-card grammar (see moodboard canvas items).
const NODE_CLASS =
  'ring-border/60 bg-card cursor-pointer rounded-lg p-3 shadow-sm ring-1 transition-shadow hover:shadow-md focus-visible:outline-none'
const NODE_SELECTED_CLASS = 'ring-primary ring-offset-background ring-2 ring-offset-2'

function VerticalConnector({ dashed }: { dashed?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'mx-auto block h-6 w-px shrink-0',
        dashed ? 'border-border border-l border-dashed' : 'bg-border',
      )}
    />
  )
}

/**
 * Half-width horizontal bus segments per cell build the classic CSS org-chart
 * tree without measuring: first cell draws only its right half, last only its
 * left half, middles the full width. A single cell degenerates to the stub.
 */
function BusSegment({
  first,
  last,
  edge,
}: {
  first: boolean
  last: boolean
  edge: 'top' | 'bottom'
}) {
  const single = first && last
  return (
    <span aria-hidden className="relative block h-4 w-full">
      {!single && (
        <span
          className={cn(
            'bg-border absolute h-px',
            edge === 'top' ? 'top-0' : 'bottom-0',
            first ? 'left-1/2 right-0' : last ? 'left-0 right-1/2' : 'left-0 right-0',
          )}
        />
      )}
      <span
        className={cn(
          'bg-border absolute left-1/2 h-4 w-px',
          edge === 'top' ? 'top-0' : 'bottom-0',
        )}
      />
    </span>
  )
}

export function AutomationFlowCanvas({
  days,
  timezone,
  source,
  platforms,
  selected,
  onSelect,
  onAddPlatform,
  onRemovePlatform,
  className,
}: Props) {
  const paneRef = useRef<HTMLDivElement>(null)
  const ghostRef = useRef<HTMLDivElement>(null)

  const selectedPlatforms = AUTOMATION_PLATFORMS.filter((p) => platforms.includes(p))
  const remaining = AUTOMATION_PLATFORMS.filter((p) => !platforms.includes(p))
  const isLastPlatform = selectedPlatforms.length === 1

  const addPlatform = (platform: AutomationPlatform) => {
    // Ripple from where the new node lands — the ghost node's spot.
    const pane = paneRef.current?.getBoundingClientRect()
    const ghost = ghostRef.current?.getBoundingClientRect()
    if (pane && ghost) {
      emitDotRipple({
        x: ghost.left - pane.left,
        y: ghost.top - pane.top,
        w: ghost.width,
        h: ghost.height,
      })
    }
    onAddPlatform(platform)
  }

  return (
    <div ref={paneRef} className={cn('bg-background relative overflow-hidden', className)}>
      <DotGridLayer paneRef={paneRef} />

      <div className="relative h-full overflow-auto">
        <div className="flex min-h-full min-w-max flex-col items-center justify-center px-8 py-10">
          {/* TRIGGER */}
          <button
            type="button"
            aria-pressed={selected === 'trigger'}
            onClick={() => onSelect('trigger')}
            className={cn(NODE_CLASS, 'w-56', selected === 'trigger' && NODE_SELECTED_CLASS)}>
            <span className="flex items-start gap-2">
              <Clock className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
              <span className="text-foreground line-clamp-2 text-sm font-semibold">
                Every {formatDayList(days, { short: true })}
              </span>
            </span>
            <span className="text-muted-foreground mt-1 block pl-6 text-xs">8:00 · {timezone}</span>
          </button>

          <VerticalConnector />

          {/* GENERATE */}
          <button
            type="button"
            aria-pressed={selected === 'generate'}
            onClick={() => onSelect('generate')}
            className={cn(NODE_CLASS, 'w-56', selected === 'generate' && NODE_SELECTED_CLASS)}>
            <span className="flex items-center gap-2">
              <StarsIcon width={14} height={14} color="var(--foreground)" />
              <span className="text-foreground text-sm font-semibold">Generate a post</span>
            </span>
            <span className="mt-1.5 block pl-6">
              {source && <SourceChip source={source} size="sm" />}
            </span>
          </button>

          <VerticalConnector />

          {/* PLATFORM fan-out → nodes → fan-in. The selected-platform nodes form
              their OWN centered row so a single platform sits dead-center under
              Generate (straight vertical line). The "+ Platform" ghost is pulled
              OUT of the flow — absolutely positioned to the right of the row and
              vertically aligned to the node box center — so it never shifts the
              tree's horizontal centering. */}
          <div className="relative">
            <div className="flex justify-center">
              <AnimatePresence initial={false}>
                {selectedPlatforms.map((platform, i) => {
                  const isSelected = selected === `platform:${platform}`
                  const first = i === 0
                  const last = i === selectedPlatforms.length - 1
                  return (
                    <motion.div
                      key={platform}
                      layout
                      initial={{ opacity: 0, scale: 0.94 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.94 }}
                      transition={{ duration: 0.24, ease: SNAP_CURVE }}
                      className="flex w-32 flex-col items-center">
                      <BusSegment first={first} last={last} edge="top" />
                      <div className="group relative">
                        <button
                          type="button"
                          aria-pressed={isSelected}
                          onClick={() => onSelect(`platform:${platform}`)}
                          className={cn(
                            NODE_CLASS,
                            'flex w-28 flex-col items-center gap-1.5 py-3',
                            isSelected && NODE_SELECTED_CLASS,
                          )}>
                          <PlatformIcon platform={platform} size="sm" />
                          <span className="text-foreground text-xs font-medium">
                            {PLATFORM_LABELS[platform]}
                          </span>
                        </button>
                        <button
                          type="button"
                          disabled={isLastPlatform}
                          onClick={() => onRemovePlatform(platform)}
                          aria-label={`Remove ${PLATFORM_LABELS[platform]}`}
                          title={isLastPlatform ? 'At least one platform required' : undefined}
                          className="border-border bg-background text-muted-foreground hover:text-foreground absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border opacity-0 shadow-sm transition-opacity focus-visible:opacity-100 disabled:cursor-not-allowed group-hover:opacity-100">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      <BusSegment first={first} last={last} edge="bottom" />
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </div>

            {remaining.length > 0 && (
              // top-4 lines the ghost's box top up with the platform node boxes,
              // which sit below their h-4 top BusSegment.
              <div ref={ghostRef} className="absolute left-full top-4 ml-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="border-border text-muted-foreground hover:border-primary/50 hover:text-primary flex w-28 flex-col items-center gap-1.5 rounded-lg border border-dashed py-3 text-xs font-medium transition-colors">
                      <Plus className="h-4 w-4" />
                      Platform
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {remaining.map((platform) => (
                      <DropdownMenuItem key={platform} onClick={() => addPlatform(platform)}>
                        <PlatformIcon platform={platform} size="xs" />
                        {PLATFORM_LABELS[platform]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>

          <VerticalConnector />

          {/* EMAIL */}
          <button
            type="button"
            aria-pressed={selected === 'email'}
            onClick={() => onSelect('email')}
            className={cn(NODE_CLASS, 'w-56', selected === 'email' && NODE_SELECTED_CLASS)}>
            <span className="flex items-center gap-2">
              <Mail className="text-muted-foreground h-4 w-4 shrink-0" />
              <span className="text-foreground text-sm font-semibold">Email you a review link</span>
            </span>
            <span className="text-muted-foreground mt-1 block pl-6 text-xs">
              Sent when ready to review
            </span>
          </button>

          <VerticalConnector dashed />

          {/* Future steps */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  className="border-border text-muted-foreground w-56 cursor-default rounded-lg border border-dashed py-2.5 text-center text-xs">
                  + Add step
                </span>
              </TooltipTrigger>
              <TooltipContent>Slack, WhatsApp & more coming soon</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  )
}
